import { useState } from "react";
import { toast } from "sonner";
import { Search, BookOpen, CalendarDays, Building2, ReceiptText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Eyebrow } from "@/components/Bits";
import { Reveal } from "@/components/Reveal";
import { getMyActivity } from "@/lib/api";

const StatusBadge = ({ status }) => {
  const map = {
    paid: "bg-primary text-primary-foreground",
    enrolled: "bg-accent text-accent-foreground",
    registered: "bg-accent text-accent-foreground",
    requested: "bg-secondary text-secondary-foreground",
    lead: "bg-secondary text-secondary-foreground",
  };
  const label = { lead: "pending", requested: "requested" }[status] || status;
  return <Badge className={`rounded-full capitalize ${map[status] || "bg-secondary text-secondary-foreground"}`}>{label}</Badge>;
};

const Row = ({ icon: Icon, title, subtitle, status, right }) => (
  <div className="flex items-center justify-between gap-4 border-b border-border py-4 last:border-0">
    <div className="flex items-start gap-3">
      <span className="mt-0.5 rounded-lg bg-secondary p-2 text-primary"><Icon className="h-4 w-4" /></span>
      <div>
        <p className="font-medium">{title}</p>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
    </div>
    <div className="flex items-center gap-3 text-right">
      {right && <span className="font-display text-sm font-semibold">{right}</span>}
      {status && <StatusBadge status={status} />}
    </div>
  </div>
);

export default function MyEnrolments() {
  const [email, setEmail] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setSearched(false);
    try {
      const res = await getMyActivity(email);
      setData(res);
      setSearched(true);
    } catch {
      toast.error("Couldn't fetch your activity. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const paidCourses = (data?.payments || []).filter((p) => p.item_type === "course");
  const paidStudio = (data?.payments || []).filter((p) => p.item_type === "studio");
  const paidEvents = (data?.payments || []).filter((p) => p.item_type === "event");

  return (
    <div>
      <section className="relative overflow-hidden border-b border-border noise">
        <div className="ytca-container py-16 sm:py-20">
          <Eyebrow>My YTCA</Eyebrow>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-semibold tracking-tight sm:text-6xl">Your courses & bookings.</h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground">Enter the email you used to enrol or book, and we'll show everything in one place.</p>
        </div>
      </section>

      <div className="ytca-container py-12">
        <form onSubmit={lookup} className="flex max-w-xl flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1 space-y-2">
            <Label htmlFor="me-email">Email</Label>
            <Input id="me-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="my-enrolments-email-input" />
          </div>
          <Button type="submit" disabled={loading} className="rounded-full" data-testid="my-enrolments-lookup-button">
            <Search className="h-4 w-4" />{loading ? "Looking…" : "Find My Activity"}
          </Button>
        </form>

        {searched && data && (
          <div className="mt-10 space-y-8" data-testid="my-enrolments-results">
            {!data.has_any && (
              <Card className="rounded-2xl border-border bg-card p-8 text-center">
                <p className="font-display text-xl font-semibold">Nothing found for that email yet.</p>
                <p className="mt-2 text-muted-foreground">Once you enrol in a course or book the studio, it'll appear here.</p>
              </Card>
            )}

            {(paidCourses.length > 0 || paidStudio.length > 0 || paidEvents.length > 0) && (
              <Reveal>
                <Card className="rounded-2xl border-border bg-card p-6">
                  <h2 className="flex items-center gap-2 font-display text-xl font-semibold"><ReceiptText className="h-5 w-5 text-primary" />Paid</h2>
                  <div className="mt-3">
                    {[...paidCourses, ...paidEvents, ...paidStudio].map((p) => (
                      <Row key={p.id} icon={p.item_type === "studio" ? Building2 : p.item_type === "event" ? CalendarDays : BookOpen}
                        title={p.item_title} subtitle={new Date(p.created_at).toLocaleDateString()} status="paid" right={`₹${p.amount}`} />
                    ))}
                  </div>
                </Card>
              </Reveal>
            )}

            {data.enrolments?.length > 0 && (
              <Reveal>
                <Card className="rounded-2xl border-border bg-card p-6">
                  <h2 className="flex items-center gap-2 font-display text-xl font-semibold"><BookOpen className="h-5 w-5 text-primary" />Course Enrolments</h2>
                  <div className="mt-3">
                    {data.enrolments.map((c) => (
                      <Row key={c.id} icon={BookOpen} title={c.course_title} subtitle={new Date(c.created_at).toLocaleDateString()} status={c.status} />
                    ))}
                  </div>
                </Card>
              </Reveal>
            )}

            {data.registrations?.length > 0 && (
              <Reveal>
                <Card className="rounded-2xl border-border bg-card p-6">
                  <h2 className="flex items-center gap-2 font-display text-xl font-semibold"><CalendarDays className="h-5 w-5 text-primary" />Event Registrations</h2>
                  <div className="mt-3">
                    {data.registrations.map((r) => (
                      <Row key={r.id} icon={CalendarDays} title={r.event_title} subtitle={new Date(r.created_at).toLocaleDateString()} status={r.status} />
                    ))}
                  </div>
                </Card>
              </Reveal>
            )}

            {data.bookings?.length > 0 && (
              <Reveal>
                <Card className="rounded-2xl border-border bg-card p-6">
                  <h2 className="flex items-center gap-2 font-display text-xl font-semibold"><Building2 className="h-5 w-5 text-primary" />Studio Bookings</h2>
                  <div className="mt-3">
                    {data.bookings.map((b) => (
                      <Row key={b.id} icon={Building2} title={b.offering_title} subtitle={b.preferred_date || new Date(b.created_at).toLocaleDateString()} status={b.status} />
                    ))}
                  </div>
                </Card>
              </Reveal>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
